import { updateQueueFilters } from "api/account/client";
import { BlueOutlineButton } from "components/components/BlueOutlineButton/BlueOutlineButton";
import Button from "components/components/Button/Button";
import CheckboxListContainer, {
  CheckboxDataState,
  CheckboxState,
} from "components/components/CheckboxList/CheckboxListContainer";
import { queueFilterCheckboxListDefinition } from "components/components/Iris/AuthenticatedView/IrisDetailsPage/IrisDetails/QueueInfoCard/QueueFilters/queueFilterCategories";
import { FiltersModalSection } from "components/components/Iris/AuthenticatedView/IrisDetailsPage/IrisDetails/QueueInfoCard/QueueFilters/style";
import { LoadingSpinnerContainer } from "components/components/Iris/commonStyle";
import LoadingSpinner from "components/components/LoadingSpinner/LoadingSpinner";
import Modal, { ModalSize } from "components/components/Modal/Modal";
import {
  ModalButtonContainer,
  ModalPadding,
  ModalSection,
  ModalSubHeader,
} from "components/components/Modal/commonStyle";
import WarningContainer from "components/components/WarningContainer/WarningContainer";
import {
  getDefaultCheckboxStates,
  ListClickEvent,
  updateCheckboxStatesOnClick,
} from "hooks/useCheckboxList";
import {
  RequestStatus,
  useRequestWithDefaultErrorHandling,
} from "hooks/useRequest";
import React, { useEffect, useState } from "react";

const mapCheckboxStatesToDatasets = (
  checkboxStates: CheckboxDataState[]
): string[] =>
  checkboxStates.reduce<string[]>((acc, state) => {
    if (state.checkboxState === CheckboxState.CHECKED && state.datasets) {
      return [...acc, ...state.datasets];
    }
    return acc;
  }, []);

interface Props {
  queueTitle: string;
  appliedFilters: string[];
  isModalActive: boolean;
  setIsModalActive: React.Dispatch<React.SetStateAction<boolean>>;
  fetchIrisDetails: () => void;
}

export const QueueFiltersSelector: React.FC<Props> = ({
  queueTitle,
  appliedFilters,
  isModalActive,
  setIsModalActive,
  fetchIrisDetails,
}) => {
  const [checkboxStates, setCheckboxStates] = useState(
    getDefaultCheckboxStates(queueFilterCheckboxListDefinition, appliedFilters)
  );

  useEffect(() => {
    setCheckboxStates(
      getDefaultCheckboxStates(
        queueFilterCheckboxListDefinition,
        appliedFilters
      )
    );
  }, [appliedFilters]);

  const handleCheckboxListClick = (clickEvent: ListClickEvent): void => {
    const newCheckboxStates = updateCheckboxStatesOnClick(
      checkboxStates,
      clickEvent
    );
    setCheckboxStates(newCheckboxStates);
  };

  const { request: applyFiltersRequest, status: applyFiltersStatus } =
    useRequestWithDefaultErrorHandling(
      updateQueueFilters,
      "Sorry, something went wrong when updating the filters."
    );

  const updateFilters = async (): Promise<void> => {
    const datasetsToFilter = mapCheckboxStatesToDatasets(checkboxStates);
    await applyFiltersRequest(queueTitle, datasetsToFilter);
    setIsModalActive(false);
    fetchIrisDetails();
  };

  return (
    <Modal
      size={ModalSize.large}
      isActive={isModalActive}
      onClose={(): void => setIsModalActive(false)}
    >
      <ModalPadding>
        {applyFiltersStatus === RequestStatus.IN_PROGRESS ? (
          <LoadingSpinnerContainer>
            <LoadingSpinner isLoading />
            <p>Updating filters...</p>
          </LoadingSpinnerContainer>
        ) : (
          <>
            <ModalSection>
              <h2>Select the filters you want to apply</h2>
            </ModalSection>
            <ModalSection>
              <FiltersModalSection>
                <ModalSubHeader>Filters</ModalSubHeader>
                <CheckboxListContainer
                  listId={queueTitle}
                  checkboxDataStates={checkboxStates}
                  registerClickForListItem={handleCheckboxListClick}
                />
              </FiltersModalSection>
            </ModalSection>
            <WarningContainer>
              Any{" "}
              <strong>
                messages that have already built up in your queue will be
                unaffected
              </strong>{" "}
              by changing what filters are applied now.
            </WarningContainer>
            <hr />
            <ModalSection className="centre">
              <ModalButtonContainer>
                <BlueOutlineButton
                  ariaLabel="Cancel"
                  buttonText="cancel"
                  className="auto-width"
                  onClick={(): void => setIsModalActive(false)}
                />
                <Button
                  ariaLabel="Apply"
                  buttonText="apply"
                  onClick={updateFilters}
                  dataTestId="apply-filters-button"
                />
              </ModalButtonContainer>
            </ModalSection>
          </>
        )}
      </ModalPadding>
    </Modal>
  );
};
